import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import SearchBar from '../components/SearchBar';
import { getSummaryStats } from '../services/api';

// Display names for disorders
const DISORDER_DISPLAY_NAMES = {
  'asc': 'ASC (Autism)',
  'schema': 'SCHEMA (Schizophrenia)',
  'bipex': 'BipEx (Bipolar Disorder)',
  'epi25': 'Epi25 (Epilepsy)',
  'mdd': 'MDD (Major Depressive Disorder)',
  'ndd': 'NDD (Neurodevelopmental Disorders)',
  'adhd': 'ADHD (Attention Deficit Hyperactivity Disorder)',
  'cognition': 'Cognition',
  'ocd': 'OCD (Obsessive-Compulsive Disorder)'
};

const HomePage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getSummaryStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Get all disorders from stats, sorted by display name
  const disorders = stats?.disorders ? Object.keys(stats.disorders)
    .filter(disorder => stats.disorders[disorder]?.genes_with_data > 0)
    .sort((a, b) => {
      const nameA = DISORDER_DISPLAY_NAMES[a] || a;
      const nameB = DISORDER_DISPLAY_NAMES[b] || b;
      return nameA.localeCompare(nameB);
    }) : [];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Genetics Database Webapp
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Query rare variant genetic associations across psychiatric disorders
        </Typography>
      </Box>

      <Box sx={{ mb: 6 }}>
        <SearchBar />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : stats && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Database Summary
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Total genes: {stats.total_genes?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Studies/Traits with data: {disorders.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {disorders.map((disorder) => {
            const displayName = DISORDER_DISPLAY_NAMES[disorder] || disorder.toUpperCase();
            const disorderStats = stats.disorders[disorder];
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={disorder}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Genes with data: {disorderStats?.genes_with_data?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                      Significant (p &lt; 0.05): {disorderStats?.significant_genes?.toLocaleString() || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default HomePage;

